import { useMutation, useQueryClient } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import type { SuccessResponse } from '@pray/shared/utils/api/types/response';

import type { ChatbotPromptResource } from './getChatbotPrompt';

type UpdateChatbotPromptParams = {
  handler: string;
  type: string;
  text: string;
};

type UpdateChatbotPromptResource = {
  handlerType: string;
  promptType: string;
  updated: boolean;
};

export async function updateChatbotPrompt({
  handler,
  type,
  text,
}: UpdateChatbotPromptParams): Promise<UpdateChatbotPromptResource> {
  if (!handler) throw Error('Handler is required');
  if (!type) throw Error('Type is required');
  if (!text) throw Error('Text is required');

  const url = `/web/studio/conversations/settings/prompts`;
  const body = { handler, type, text };
  const response = await api.executePutRequest<SuccessResponse<UpdateChatbotPromptResource>>(url, body);

  return response.data.data[0].resource;
}

export function useUpdateChatbotPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateChatbotPrompt,
    onSuccess: (_resource, { handler, type, text }) => {
      // Write the saved text first: a form that resets on save would otherwise read the old cache
      // and show the previous text until the refetch lands.
      queryClient.setQueryData<ChatbotPromptResource>(queryKeys.chatbotPrompt(handler, type), (current) =>
        current ? { ...current, text } : current
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.chatbotPrompt() });
    },
  });
}
